import { ApolloClient, ApolloLink, HttpLink, InMemoryCache, Observable } from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { cfgApiPlayerSubscriptionUrl, cfgApiPlayerUrl, cfgApiPublicSubscriptionUrl, cfgApiPublicUrl, cfgChatApiUrl, cfgChatSubscriptionUrl, cfgNotifyApiUrl, cfgNotifySubscriptionUrl } from '@infinitro/config';
import { createClient } from 'graphql-ws';
import { getCurrentToken } from './auth';
export var Api;
(function (Api) {
    Api["chat"] = "chat";
    Api["notify"] = "notify";
    Api["player"] = "player";
    Api["public"] = "public";
})(Api || (Api = {}));
export var LinkType;
(function (LinkType) {
    LinkType["Http"] = "Http";
    LinkType["WebSocket"] = "WebSocket";
})(LinkType || (LinkType = {}));
const authLink = setContext((_, { headers }) => {
    const accessToken = getCurrentToken();
    if (!accessToken)
        return { headers };
    return {
        headers: {
            ...headers,
            'x-access-token': accessToken
        }
    };
});
const getApiUrl = (api, linkType) => {
    switch (linkType) {
        case LinkType.Http:
            switch (api) {
                case Api.chat:
                    return cfgChatApiUrl();
                case Api.player:
                    return cfgApiPlayerUrl();
                case Api.public:
                    return cfgApiPublicUrl();
                case Api.notify:
                    return cfgNotifyApiUrl();
                default:
                    throw new Error(`Invalid api: ${api}`);
            }
        case LinkType.WebSocket:
            switch (api) {
                case Api.chat:
                    return cfgChatSubscriptionUrl();
                case Api.player:
                    return cfgApiPlayerSubscriptionUrl();
                case Api.public:
                    return cfgApiPublicSubscriptionUrl();
                case Api.notify:
                    return cfgNotifySubscriptionUrl();
                default:
                    throw new Error(`Invalid api: ${api}`);
            }
        default:
            throw new Error(`Invalid linkType: ${linkType}`);
    }
};
const httpChatLink = new HttpLink({
    uri: getApiUrl(Api.chat, LinkType.Http),
    credentials: 'same-origin'
});
const httpNotifyLink = new HttpLink({
    uri: getApiUrl(Api.notify, LinkType.Http),
    credentials: 'same-origin'
});
const httpPlayerLink = new HttpLink({
    uri: getApiUrl(Api.player, LinkType.Http),
    credentials: 'same-origin'
});
const httpPublicLink = new HttpLink({
    uri: getApiUrl(Api.public, LinkType.Http),
    credentials: 'same-origin'
});
const wsChatLink = new GraphQLWsLink(createClient({ url: getApiUrl(Api.chat, LinkType.WebSocket) }));
const wsNotifyLink = new GraphQLWsLink(createClient({ url: getApiUrl(Api.notify, LinkType.WebSocket) }));
const wsPublicLink = new GraphQLWsLink(createClient({ url: getApiUrl(Api.public, LinkType.WebSocket) }));
const wsPlayerLink = new GraphQLWsLink(createClient({ url: getApiUrl(Api.player, LinkType.WebSocket) }));
const getApiLink = (api, isSubscription) => {
    if (isSubscription) {
        switch (api) {
            case Api.chat:
                return authLink.concat(wsChatLink);
            case Api.notify:
                return authLink.concat(wsNotifyLink);
            case Api.player:
                return authLink.concat(wsPlayerLink);
            case Api.public:
                return authLink.concat(wsPublicLink);
        }
    }
    switch (api) {
        case Api.chat:
            return authLink.concat(httpChatLink);
        case Api.notify:
            return authLink.concat(httpNotifyLink);
        case Api.player:
            return authLink.concat(httpPlayerLink);
        case Api.public:
        default:
            return authLink.concat(httpPublicLink);
    }
};
const customLink = new ApolloLink((operation, forward) => {
    const definition = getMainDefinition(operation.query);
    const api = operation.getContext().api;
    const isSubscription = definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
    const link = getApiLink(api, isSubscription);
    if (!link) {
        console.log(`no link for ${api}`);
        return forward(operation);
    }
    if (isSubscription) {
        return new Observable((observer) => {
            const subscription = link.request(operation).subscribe({
                next: observer.next.bind(observer),
                error: observer.error.bind(observer),
                complete: observer.complete.bind(observer)
            });
            return () => subscription.unsubscribe();
        });
    }
    return link.request(operation);
});
const cache = new InMemoryCache();
const client = new ApolloClient({
    link: customLink,
    cache
});
export { client };
export default client;
